import { Button } from '@saay/shadcn/components/button';
import { Checkbox } from '@saay/shadcn/components/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@saay/shadcn/components/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@saay/shadcn/components/popover';
import { Skeleton } from '@saay/shadcn/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@saay/shadcn/components/table';
import { useWindowClass } from '../kit.js';
import { ArrowDownIcon, ArrowUpIcon, ArrowUpDownIcon, MoreHorizontalIcon } from 'lucide-react';
import { type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';

import { ListEmpty } from './list-state.js';

export type DataTableColumn<Row> = {
  readonly enableHiding?: boolean;
  readonly header: ReactNode;
  readonly id: string;
  readonly numeric?: boolean;
  readonly pin?: 'start' | 'end';
  readonly render: (row: Row) => ReactNode;
  readonly sortable?: boolean;
};

export type DataTableSort = {
  readonly columnId: string;
  readonly direction: 'asc' | 'desc';
};

export type DataTableRowAction<Row> = {
  readonly destructive?: boolean;
  readonly disabled?: boolean;
  readonly disabledReason?: string;
  readonly label: string;
  readonly onSelect: (row: Row) => void;
};

/** Two-line summary on compact widths (XAP-05). */
export type AdminListMobileSummary<Row> = (row: Row) => {
  readonly primary: ReactNode;
  readonly secondary: ReactNode;
};

function sortAria(
  columnId: string,
  sort: DataTableSort | null | undefined,
): 'ascending' | 'descending' | 'none' {
  if (sort === null || sort === undefined || sort.columnId !== columnId) {
    return 'none';
  }
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}

function RowActions<Row>({
  actions,
  row,
}: {
  readonly actions: readonly DataTableRowAction<Row>[];
  readonly row: Row;
}) {
  if (actions.length === 0) {
    return null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button aria-label="عملیات ردیف" size="icon-sm" variant="ghost" />}
      >
        <MoreHorizontalIcon aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action) => (
          <DropdownMenuItem
            disabled={action.disabled === true}
            key={action.label}
            onClick={() => action.onSelect(row)}
            variant={action.destructive === true ? 'destructive' : 'default'}
          >
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function visibleColumns<Row>(
  columns: readonly DataTableColumn<Row>[],
  visibleColumnIds: readonly string[] | undefined,
): readonly DataTableColumn<Row>[] {
  if (visibleColumnIds === undefined) {
    return columns;
  }
  return columns.filter((column) => visibleColumnIds.includes(column.id));
}

function ColumnPicker<Row>({
  columns,
  label,
  onChange,
  visibleColumnIds,
}: {
  readonly columns: readonly DataTableColumn<Row>[];
  readonly label: string;
  readonly onChange: (columnIds: readonly string[]) => void;
  readonly visibleColumnIds?: readonly string[];
}) {
  const hideable = columns.filter((column) => column.enableHiding !== false);
  const shown = visibleColumnIds ?? columns.map((column) => column.id);
  return (
    <Popover>
      <PopoverTrigger render={<Button size="sm" type="button" variant="outline" />}>
        {label}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56">
        <div className="flex flex-col gap-2">
          {hideable.map((column) => {
            const checked = shown.includes(column.id);
            return (
              <label className="flex items-center gap-2" key={column.id}>
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => {
                    const next = checked
                      ? shown.filter((id) => id !== column.id)
                      : [...shown, column.id];
                    onChange(next);
                  }}
                />
                <span>{column.header}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function rowClick<Row>(
  onRowClick: ((row: Row) => void) | undefined,
  row: Row,
): {
  readonly onClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLTableRowElement>) => void;
  readonly tabIndex?: number;
} {
  if (onRowClick === undefined) {
    return {};
  }
  return {
    onClick: (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('a, button, input, select, textarea, [role="menuitem"]') !== null) {
        return;
      }
      onRowClick(row);
    },
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onRowClick(row);
      }
    },
    tabIndex: 0,
  };
}

function AdminMobileList<Row>({
  caption,
  emptyMessage,
  loading,
  mobileSummary,
  onRowClick,
  rowActions,
  rowKey,
  rows,
}: {
  readonly caption: string;
  readonly emptyMessage?: ReactNode;
  readonly loading?: boolean;
  readonly mobileSummary: AdminListMobileSummary<Row>;
  readonly onRowClick?: (row: Row) => void;
  readonly rowActions?: (row: Row) => readonly DataTableRowAction<Row>[];
  readonly rowKey: (row: Row) => string;
  readonly rows: readonly Row[];
}) {
  if (loading) {
    return (
      <div aria-busy="true" aria-label={caption} className="flex flex-col gap-2" role="list">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton className="h-14 w-full" key={`sk-${index}`} />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <ListEmpty
        title={typeof emptyMessage === 'string' ? emptyMessage : 'موردی برای نمایش نیست.'}
      />
    );
  }
  return (
    <ul aria-label={caption} className="flex flex-col gap-2" role="list">
      {rows.map((row) => {
        const summary = mobileSummary(row);
        const actions = rowActions?.(row) ?? [];
        const clickable = onRowClick !== undefined;
        return (
          <li
            className="flex items-center gap-2 rounded-2xl bg-background p-3"
            key={rowKey(row)}
            role="listitem"
          >
            {clickable ? (
              <button
                className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-start"
                onClick={() => onRowClick(row)}
                type="button"
              >
                <span className="font-bold">{summary.primary}</span>
                <span className="text-xs text-muted-foreground">{summary.secondary}</span>
              </button>
            ) : (
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-bold">{summary.primary}</span>
                <span className="text-xs text-muted-foreground">{summary.secondary}</span>
              </div>
            )}
            <RowActions actions={actions} row={row} />
          </li>
        );
      })}
    </ul>
  );
}

/** Data table on desktop; two-line list on compact (XAP-05). */
export function AdminListTable<Row>({
  caption,
  columnPickerLabel = 'ستون‌ها',
  columns,
  emptyMessage,
  loading,
  mobileSummary,
  onRowClick,
  onSortChange,
  onVisibleColumnIdsChange,
  regionLabel,
  rowActions,
  rowKey,
  rows,
  sort,
  visibleColumnIds,
}: {
  readonly caption: string;
  /** The scroll region's name when a titled section around the table uses the caption. */
  readonly regionLabel?: string;
  readonly columnPickerLabel?: string;
  readonly columns: readonly DataTableColumn<Row>[];
  readonly emptyMessage?: ReactNode;
  readonly loading?: boolean;
  readonly mobileSummary: AdminListMobileSummary<Row>;
  readonly onRowClick?: (row: Row) => void;
  readonly onSortChange?: (sort: DataTableSort | null) => void;
  readonly onVisibleColumnIdsChange?: (columnIds: readonly string[]) => void;
  readonly rowActions?: (row: Row) => readonly DataTableRowAction<Row>[];
  readonly rowKey: (row: Row) => string;
  readonly rows: readonly Row[];
  readonly sort?: DataTableSort | null;
  readonly visibleColumnIds?: readonly string[];
}) {
  const windowClass = useWindowClass('expanded');
  if (windowClass === 'compact') {
    return (
      <AdminMobileList
        caption={caption}
        {...(emptyMessage === undefined ? {} : { emptyMessage })}
        {...(loading === undefined ? {} : { loading })}
        mobileSummary={mobileSummary}
        {...(onRowClick === undefined ? {} : { onRowClick })}
        {...(rowActions === undefined ? {} : { rowActions })}
        rowKey={rowKey}
        rows={rows}
      />
    );
  }
  const shown = visibleColumns(columns, visibleColumnIds);
  const hideable = columns.filter((column) => column.enableHiding !== false);
  const showPicker = hideable.length > 0 && onVisibleColumnIdsChange !== undefined;
  return (
    <div className="flex flex-col gap-2">
      {showPicker ? (
        <div className="flex justify-end">
          <ColumnPicker
            columns={columns}
            label={columnPickerLabel}
            onChange={onVisibleColumnIdsChange}
            {...(visibleColumnIds === undefined ? {} : { visibleColumnIds })}
          />
        </div>
      ) : null}
      {/* A focusable region lets keyboard users scroll the table horizontally. */}
      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div
        aria-busy={loading || undefined}
        aria-label={regionLabel ?? caption}
        className="w-full overflow-x-auto rounded-2xl bg-background [&_td:first-child]:ps-5 [&_td:last-child]:pe-5 [&_th:first-child]:ps-5 [&_th:last-child]:pe-5"
        role="region"
        tabIndex={0}
      >
        <Table>
          <caption className="sr-only">{caption}</caption>
          <TableHeader>
            <TableRow>
              {shown.map((column) => {
                const sortable = column.sortable === true && onSortChange !== undefined;
                return (
                  <TableHead
                    aria-sort={sortable ? sortAria(column.id, sort) : undefined}
                    className={column.numeric === true ? 'text-end' : undefined}
                    key={column.id}
                    scope="col"
                  >
                    {sortable ? (
                      <button
                        className="inline-flex items-center gap-1"
                        onClick={() => {
                          if (sort?.columnId === column.id) {
                            onSortChange(
                              sort.direction === 'asc'
                                ? { columnId: column.id, direction: 'desc' }
                                : null,
                            );
                          } else {
                            onSortChange({ columnId: column.id, direction: 'asc' });
                          }
                        }}
                        type="button"
                      >
                        {column.header}
                        {sort?.columnId === column.id ? (
                          sort.direction === 'asc' ? (
                            <ArrowUpIcon aria-hidden="true" />
                          ) : (
                            <ArrowDownIcon aria-hidden="true" />
                          )
                        ) : (
                          <ArrowUpDownIcon aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                );
              })}
              {rowActions === undefined ? null : (
                <TableHead scope="col">
                  <span className="sr-only">عملیات</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }, (_, index) => (
                <TableRow key={`sk-${index}`}>
                  {shown.map((column) => (
                    <TableCell key={column.id}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={shown.length + (rowActions === undefined ? 0 : 1)}>
                  <ListEmpty
                    title={
                      typeof emptyMessage === 'string' ? emptyMessage : 'موردی برای نمایش نیست.'
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={rowKey(row)} {...rowClick(onRowClick, row)}>
                  {shown.map((column) => (
                    <TableCell
                      className={
                        column.numeric === true
                          ? 'text-end whitespace-normal! tabular-nums'
                          : 'whitespace-normal!'
                      }
                      key={column.id}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                  {rowActions === undefined ? null : (
                    <TableCell>
                      <RowActions actions={rowActions(row)} row={row} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
